import anime from 'animejs'
import { registerFunctionalComponent } from 'js/lib/components/component'
import { initConfetti } from 'js/lib/confetti'

// ─── Timing ──────────────────────────────────────────────────────────────────
const FRAME_1_HOLD      = 5900  // how long frame 1 stays before exit begins
const COUNTER_DURATION  = 3000  // digit roll duration
const SHADOW_FADE_IN    = 500   // shadow appears before counter pops in
const COUNTER_POP_IN    = 700   // counter entrance after shadow
const COUNTER_EXIT      = 1050  // total duration of counter exit (bounce + fade)
const FRAME_2_OFFSET    = 300   // gap between frame 1 exit and frame 2 enter

registerFunctionalComponent('.js-c-promo___hero__million_pets', 'PromoHeroMillionPetsComponent', (component) => {
  const frame1 = component.element.querySelector('.js-frame-1')
  const frame2 = component.element.querySelector('.js-frame-2')

  if (!frame1 || !frame2) return

  const counter = frame1.querySelector('.js-counter')
  const shadow  = frame1.querySelector('.js-counter-shadow')

  // ─── Card layout ───────────────────────────────────────────────────────────
  // Read positions while frame2 is briefly visible (before paint), then hide it
  frame2.style.opacity = '0'
  frame2.classList.remove('tw-hidden')

  const cards = Array.from(frame2.querySelectorAll('.js-card'))
  const centerX = frame2.offsetWidth / 2
  const centerY = frame2.offsetHeight / 2

  const cardData = cards.map((card) => {
    const matrix = new DOMMatrix(getComputedStyle(card).transform)
    const targetTX = matrix.m41
    const targetTY = matrix.m42
    const targetRotate = Math.atan2(matrix.m21, matrix.m11) * (180 / Math.PI)
    const startDX = centerX - card.offsetLeft - card.offsetWidth / 2
    const startDY = centerY - card.offsetTop - card.offsetHeight / 2
    return { startDX, startDY, targetTX, targetTY, targetRotate }
  })

  cards.forEach((card, i) => {
    anime.set(card, { translateX: cardData[i].startDX, translateY: cardData[i].startDY, rotate: 0 })
  })

  frame2.classList.add('tw-hidden')

  const group1 = cards.slice(0, 3)
  const group2 = cards.slice(3, 6)

  // ─── Counter digit roll ────────────────────────────────────────────────────
  // Digits stop sequentially (left → right) by using staggered durations.
  // spin_order 0 = first to stop, 2 = last to stop (at full COUNTER_DURATION).
  const SPIN_DURATION_RATIOS = [0.5, 1, 2]

  function animateCounter() {
    component.element.querySelectorAll('.js-counter-digit').forEach(digitEl => {
      const targetDigit = parseInt(digitEl.dataset.targetDigit, 10)
      const spinOrder   = parseInt(digitEl.dataset.spinOrder, 10) || 0
      const spinner     = digitEl.querySelector('.tw-flex-col')
      const digitHeight = spinner.offsetHeight / 10
      const duration    = COUNTER_DURATION * (SPIN_DURATION_RATIOS[spinOrder] ?? 1.0)

      anime.set(spinner, { translateY: 0 })
      anime({ targets: spinner, translateY: -(targetDigit * digitHeight), duration, easing: 'easeOutCubic' })
    })
  }

  // ─── Timeline ──────────────────────────────────────────────────────────────
  anime.timeline()

    // ── Frame 1 in ───────────────────────────────────────────────────────────
    .add({
      targets: frame1,
      keyframes: [
        { opacity: 0, duration: 0 },
        { opacity: 1, duration: 300, easing: 'easeInOutQuad' },
      ],
      begin: () => {
        frame1.style.opacity = '0'
        frame1.classList.remove('tw-hidden')
        animateCounter()

        // Shadow fades in, then counter pops up from it
        if (shadow) {
          anime.set(shadow, { opacity: 0 })
          anime({ targets: shadow, opacity: 1, duration: SHADOW_FADE_IN, easing: 'easeOutQuad' })
        }
        if (counter) {
          anime.set(counter, { translateY: 160, scale: 0.15, opacity: 0 })
          anime({
            targets: counter,
            keyframes: [
              { translateY: -12, scale: 1.08, opacity: 1, duration: COUNTER_POP_IN * 0.75, easing: 'easeOutCubic' },
              { translateY: 0,   scale: 1.00, opacity: 1, duration: COUNTER_POP_IN * 0.25, easing: 'easeInOutQuad' },
            ],
            delay: SHADOW_FADE_IN,
          })
        }
      },
    })

    // ── Frame 1 out ──────────────────────────────────────────────────────────
    // Counter bounces/pops then slides down and fades out
    .add({
      targets: counter,
      keyframes: [
        { scale: 1.18, translateY: 0,   opacity: 1, duration: 300, easing: 'easeOutQuad' },
        { scale: 1.00, translateY: 0,   opacity: 1, duration: 200, easing: 'easeInQuad'  },
        { scale: 1.10, translateY: 0,   opacity: 1, duration: 200, easing: 'easeOutQuad' },
        { scale: 0.7,  translateY: 80,  opacity: 0, duration: 350, easing: 'easeInCubic' },
      ],
    }, FRAME_1_HOLD)
    // Shadow and frame fade out with the tail of the counter exit
    .add({
      targets: [shadow, frame1],
      opacity: [1, 0],
      duration: 400,
      easing: 'easeInQuad',
      complete: () => frame1.classList.add('tw-hidden'),
    }, FRAME_1_HOLD + 700)

    // ── Frame 2 in ───────────────────────────────────────────────────────────
    .add({
      targets: frame2,
      opacity: [0, 1],
      translateY: [20, 0],
      duration: 900,
      easing: 'easeOutElastic(1, 0.4)',
      begin: () => {
        frame2.classList.remove('tw-hidden')
        anime({
          targets: group1,
          translateX: (el, i) => cardData[i].targetTX,
          translateY: (el, i) => cardData[i].targetTY,
          rotate:     (el, i) => cardData[i].targetRotate,
          duration: 1100,
          delay: anime.stagger(120),
          easing: 'easeOutCubic',
        })
        anime({
          targets: group2,
          translateX: (el, i) => cardData[i + 3].targetTX,
          translateY: (el, i) => cardData[i + 3].targetTY,
          rotate:     (el, i) => cardData[i + 3].targetRotate,
          duration: 1100,
          delay: anime.stagger(120),
          easing: 'easeOutCirc',
        })
      },
      complete: () => initConfetti(),
    }, `+=${FRAME_2_OFFSET}`)
})
